import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { apiClient } from '@/api/client';
import { supabase } from '@/integrations/supabase/client';

interface Restaurant {
  restaurant_id: string;
  restaurant_name: string;
  email?: string;
  sms_enabled?: boolean;
  stripe_customer_id?: string;
}

interface EnhancedSmsEnableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  organisationName: string;
  onSuccess: () => void;
}

export const EnhancedSmsEnableDialog: React.FC<EnhancedSmsEnableDialogProps> = ({
  open,
  onOpenChange,
  organisationId,
  organisationName,
  onSuccess
}) => {
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orgStripeId, setOrgStripeId] = useState('');
  const [selectAll, setSelectAll] = useState(false);

  // Load restaurants when dialog opens
  useEffect(() => {
    if (open && organisationId) {
      loadRestaurantsAndStatus();
    }
  }, [open, organisationId]);

  // Select/deselect all restaurants
  useEffect(() => {
    if (restaurants.length > 0) {
      const allSelected = restaurants.every(r => r.sms_enabled);
      setSelectAll(allSelected);
    }
  }, [restaurants]);

  const loadRestaurantsAndStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Get all restaurants for this organization
      const { data: restaurantsData, error: restaurantsError } = await supabase
        .from('restaurants_master')
        .select('restaurant_id, restaurant_name, email')
        .eq('organisation_id', organisationId);

      if (restaurantsError) throw restaurantsError;

      // 2. Get current SMS status for each restaurant
      const { data: smsStatus, error: statusError } = await supabase
        .from('restaurant_sms_status')
        .select('restaurant_id, sms_enabled')
        .in('restaurant_id', restaurantsData.map(r => r.restaurant_id));

      if (statusError) throw statusError;

      // 3. Get Stripe mappings for each restaurant
      const { data: stripeMappings, error: mappingError } = await supabase
        .from('restaurant_stripe_mapping')
        .select('restaurant_id, stripe_customer_id')
        .in('restaurant_id', restaurantsData.map(r => r.restaurant_id));

      if (mappingError) throw mappingError;

      // 4. Get organization Stripe mapping
      const { data: orgMapping, error: orgMappingError } = await supabase
        .from('organisation_stripe_mapping')
        .select('stripe_customer_id')
        .eq('organisation_id', organisationId)
        .maybeSingle();

      if (orgMappingError) throw orgMappingError;
      if (orgMapping) {
        setOrgStripeId(orgMapping.stripe_customer_id || '');
      }

      // Create lookup maps
      const statusMap = Object.fromEntries(
        (smsStatus || []).map(status => [status.restaurant_id, status.sms_enabled])
      );

      const stripeMap = Object.fromEntries(
        (stripeMappings || []).map(mapping => [mapping.restaurant_id, mapping.stripe_customer_id])
      );

      // Combine data
      const combinedData = restaurantsData.map(restaurant => ({
        ...restaurant,
        sms_enabled: statusMap[restaurant.restaurant_id] || false,
        stripe_customer_id: stripeMap[restaurant.restaurant_id] || orgMapping?.stripe_customer_id || ''
      }));

      setRestaurants(combinedData);
    } catch (error) {
      console.error('Error loading restaurant data:', error);
      setError(error instanceof Error ? error.message : 'Failed to load restaurant data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRestaurant = (id: string, enabled: boolean) => {
    setRestaurants(prev =>
      prev.map(restaurant =>
        restaurant.restaurant_id === id
          ? { ...restaurant, sms_enabled: enabled }
          : restaurant
      )
    );
  };

  const handleToggleAll = (checked: boolean) => {
    setSelectAll(checked);
    setRestaurants(prev =>
      prev.map(restaurant => ({ ...restaurant, sms_enabled: checked }))
    );
  };

  const handleStripeIdChange = (id: string, value: string) => {
    setRestaurants(prev =>
      prev.map(restaurant =>
        restaurant.restaurant_id === id
          ? { ...restaurant, stripe_customer_id: value }
          : restaurant
      )
    );
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const selectedRestaurants = restaurants.filter(r => r.sms_enabled);
      
      if (selectedRestaurants.length === 0) {
        throw new Error('Please select at least one restaurant to enable SMS');
      }

      // Process each selected restaurant
      for (const restaurant of selectedRestaurants) {
        // Check if customer ID is provided
        if (restaurant.stripe_customer_id) {
          // If customer ID provided, update the mapping first
          await apiClient.post('/api/admin/stripe/update-customer-id', {
            restaurant_id: restaurant.restaurant_id,
            stripe_customer_id: restaurant.stripe_customer_id
          });
        }

        // Enable SMS for the restaurant - pass the Stripe customer ID in the request body
        await apiClient.post(`/api/restaurant-sms/enable/${restaurant.restaurant_id}`, {
          stripe_customer_id: restaurant.stripe_customer_id
        });
      }

      // Also set organization-level Stripe customer for consistency if provided
      if (orgStripeId) {
        await apiClient.post('/api/admin/stripe/update-customer-id', {
          organisation_id: organisationId,
          stripe_customer_id: orgStripeId
        });
      }

      toast({
        title: "SMS Enabled Successfully",
        description: `SMS has been enabled for ${selectedRestaurants.length} restaurant(s)`,
      });

      // Close dialog and refresh parent view
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Error enabling SMS:', error);
      setError(error instanceof Error ? error.message : 'Failed to enable SMS');
      
      toast({
        title: "Error Enabling SMS",
        description: error instanceof Error ? error.message : 'An error occurred',
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Enable SMS for {organisationName}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="ml-2">Loading restaurant data...</span>
          </div>
        ) : error ? (
          <div className="bg-destructive/10 p-4 rounded-md flex items-start">
            <AlertCircle className="h-5 w-5 text-destructive mr-2 mt-0.5" />
            <div>
              <h4 className="font-semibold">Error loading data</h4>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-md">
                <div className="mb-4">
                  <Label htmlFor="orgStripeId">Organization Stripe Customer ID (Optional)</Label>
                  <Input
                    id="orgStripeId"
                    placeholder="cus_..."
                    value={orgStripeId}
                    onChange={(e) => setOrgStripeId(e.target.value)}
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    This will be used for organization-level billing and as a default for restaurants without specific IDs
                  </p>
                </div>
                
                <div className="flex items-center mb-4">
                  <Checkbox
                    id="selectAll"
                    checked={selectAll}
                    onCheckedChange={handleToggleAll}
                  />
                  <label
                    htmlFor="selectAll"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 ml-2"
                  >
                    Select All Restaurants
                  </label>
                </div>
              </div>

              <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                {restaurants.map((restaurant) => (
                  <div key={restaurant.restaurant_id} className="p-4 border rounded-md">
                    <div className="flex items-center mb-3">
                      <Checkbox
                        id={`restaurant-${restaurant.restaurant_id}`}
                        checked={restaurant.sms_enabled}
                        onCheckedChange={(checked) => handleToggleRestaurant(restaurant.restaurant_id, !!checked)}
                      />
                      <label
                        htmlFor={`restaurant-${restaurant.restaurant_id}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 ml-2"
                      >
                        {restaurant.restaurant_name}
                      </label>
                    </div>

                    {restaurant.sms_enabled && (
                      <div className="ml-6 space-y-3">
                        <div>
                          <Label htmlFor={`stripe-${restaurant.restaurant_id}`}>
                            Stripe Customer ID (Optional)
                          </Label>
                          <Input
                            id={`stripe-${restaurant.restaurant_id}`}
                            placeholder="cus_..."
                            value={restaurant.stripe_customer_id || ''}
                            onChange={(e) => handleStripeIdChange(restaurant.restaurant_id, e.target.value)}
                            className="mt-1"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            If left empty, organization-level Stripe customer will be used
                          </p>
                        </div>
                        
                        {restaurant.email && (
                          <div className="text-xs text-muted-foreground">
                            Email: {restaurant.email}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {restaurants.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No restaurants found for this organization
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={submitting || restaurants.length === 0}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enabling SMS...
                  </>
                ) : (
                  'Enable SMS'
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};